import { useMemo } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { FinanceChannelFieldAvailability } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import { useAdminPageFilters } from '@/hooks/useAdminPageFilters';
import {
  boundsForTimeRange,
  daysForTimeRange,
  labelForTimeRange,
  shouldShowPriorComparison,
} from '@/lib/timeRangeOptions';
import { useFinanceDashboard } from '../api';
import { FinanceFilters } from '../components/FinanceFilters';
import { FinanceCountryGrossBreakdown } from '../components/FinanceCountryGrossBreakdown';
import { FinanceKpiCards } from '../components/FinanceKpiCards';
import { FinanceReconciliationBanner } from '../components/FinanceReconciliationBanner';
import { FinanceRefreshing } from '../components/FinanceNotReady';
import { OperatingExpenseTrendsCard } from '../components/OperatingExpenseTrendsCard';
import { PaymentChannelSummaryCard } from '../components/PaymentChannelSummaryCard';
import { PayoutKpiSummaryCard } from '../components/PayoutKpiSummaryCard';
import { PayoutStatusSummaryCard } from '../components/PayoutStatusSummaryCard';
import { RefundsChargebacksCard } from '../components/RefundsChargebacksCard';
import { RevenueBreakdownCard } from '../components/RevenueBreakdownCard';
import { RevenueSourceTrendsCard } from '../components/RevenueSourceTrendsCard';
import { RiskOverviewCard } from '../components/RiskOverviewCard';
import { TopCreatorsCard } from '../components/TopCreatorsCard';
import { TopRevenueSourcesCard } from '../components/TopRevenueSourcesCard';
import {
  overviewSeriesDisplayName,
  TransactionOverviewCard,
} from '../components/TransactionOverviewCard';
import { financeCountryLabel, financeCurrencyOptions } from '../lib/financeColors';
import {
  buildFinanceDashboardCsv,
  downloadTextFile,
  financeCsvFilename,
} from '../lib/exportFinanceCsv';
import { financeDashboardErrorMessage } from '../lib/financeErrorMessage';

function num(value: number | string | null | undefined): number {
  if (value == null) return 0;
  return typeof value === 'number' ? value : Number(value);
}

function availability(
  value: string | undefined,
): (typeof FinanceChannelFieldAvailability)[keyof typeof FinanceChannelFieldAvailability] {
  if (value === FinanceChannelFieldAvailability.INCOMPLETE_FEES) {
    return FinanceChannelFieldAvailability.INCOMPLETE_FEES;
  }
  if (value === FinanceChannelFieldAvailability.COMING_SOON) {
    return FinanceChannelFieldAvailability.COMING_SOON;
  }
  return FinanceChannelFieldAvailability.AVAILABLE;
}

function rangeLabelFor(range: string): string {
  return labelForTimeRange(range);
}

/**
 * Financial Management. Currency, date and country are one page-level filter
 * set synced to `?range=&country=&currency=` for shareable deep links.
 * Chart cards show date + country pickers; changing any of them updates every
 * module without unmounting the page (scroll stays put).
 */
export function FinancePage() {
  const { range, setRange, countryCode, setCountryCode, currency, setCurrency } =
    useAdminPageFilters({
      allowFinancialYear: true,
      defaultCurrency: 'AUD',
      allowedCurrencies: financeCurrencyOptions.map((option) => option.value),
    });
  const country = countryCode || undefined;

  const bounds = boundsForTimeRange(range);
  const days = bounds.days ?? daysForTimeRange(range);
  const showPriorComparison = shouldShowPriorComparison(range);

  const dashboardQuery = useFinanceDashboard(bounds, currency, country);

  const data = dashboardQuery.data;
  const dashboardBusy =
    !dashboardQuery.isError && (dashboardQuery.isPending || dashboardQuery.isPlaceholderData);
  const initialLoading = dashboardQuery.isLoading && !data;
  const initialError = dashboardQuery.isError && !data;
  const reportingCurrency = data?.currency ?? currency;
  const overviewRangeLabel = rangeLabelFor(range);

  const kpis = useMemo(
    () =>
      (data?.kpis ?? []).map((kpi) => ({
        ...(kpi.key != null ? { key: kpi.key } : {}),
        label: kpi.label ?? '—',
        amount: kpi.amount == null ? null : num(kpi.amount),
        delta:
          !showPriorComparison || kpi.deltaPercent == null ? null : num(kpi.deltaPercent),
        ...(kpi.description != null ? { description: kpi.description } : {}),
        ...(kpi.availability != null ? { availability: availability(kpi.availability) } : {}),
        ...(kpi.note != null ? { note: kpi.note } : {}),
      })),
    [data?.kpis, showPriorComparison],
  );

  const transactionSeries = useMemo(
    () =>
      (data?.transactionOverview?.series ?? []).map((series) => ({
        name: series.name ?? 'Series',
        ...(series.category != null ? { category: series.category } : {}),
        total: num(series.total),
        values: (series.values ?? []).map(num),
      })),
    [data?.transactionOverview?.series],
  );

  const revenueSources = useMemo(
    () =>
      (data?.revenueSources ?? []).map((source) => ({
        name: source.name ?? source.category ?? 'Other',
        ...(source.category != null ? { category: source.category } : {}),
        amount: num(source.amount),
        percentage: num(source.percentage),
      })),
    [data?.revenueSources],
  );

  const topRevenueSources = useMemo(
    () =>
      (data?.revenueSources ?? []).map((source) => ({
        name: source.name ?? source.category ?? 'Other',
        ...(source.category != null ? { category: source.category } : {}),
        amount: num(source.amount),
        percentage: num(source.percentage),
      })),
    [data?.revenueSources],
  );

  const revenueTrendSeries = useMemo(
    () =>
      (data?.revenueSourceTrends?.series ?? []).map((series) => ({
        name: series.name ?? 'Series',
        ...(series.category != null ? { category: series.category } : {}),
        total: num(series.total),
        values: (series.values ?? []).map(num),
      })),
    [data?.revenueSourceTrends?.series],
  );

  const topCreators = useMemo(
    () =>
      (data?.topCreators ?? [])
        .filter((creator) => creator.userId != null)
        .map((creator) => ({
          userId: Number(creator.userId),
          displayName: creator.displayName ?? creator.username ?? `Creator ${creator.userId}`,
          ...(creator.username != null ? { username: creator.username } : {}),
          ...(creator.avatarUrl != null ? { avatarUrl: creator.avatarUrl } : {}),
          amount: num(creator.amount),
        })),
    [data?.topCreators],
  );

  const paymentChannels = useMemo(
    () =>
      (data?.paymentChannels ?? []).map((channel) => ({
        provider: channel.provider ?? 'UNKNOWN',
        label: channel.label ?? channel.provider ?? 'Channel',
        gross: num(channel.gross),
        grossAvailability: availability(channel.grossAvailability),
        providerFee: channel.providerFee == null ? null : num(channel.providerFee),
        providerFeeAvailability: availability(channel.providerFeeAvailability),
        refundsChargebacks:
          channel.refundsChargebacks == null ? null : num(channel.refundsChargebacks),
        refundsChargebacksAvailability: availability(channel.refundsChargebacksAvailability),
        netSettlement: channel.netSettlement == null ? null : num(channel.netSettlement),
        netSettlementAvailability: availability(channel.netSettlementAvailability),
        ...(channel.note != null ? { note: channel.note } : {}),
      })),
    [data?.paymentChannels],
  );

  const payoutStatusSlices = useMemo(() => {
    const summary = data?.payoutStatusSummary;
    return [
      {
        key: 'PAID',
        label: 'Paid',
        amount: summary?.paid == null ? null : num(summary.paid),
        availability: availability(summary?.paidAvailability),
      },
      {
        key: 'PENDING',
        label: 'Pending',
        amount: summary?.pending == null ? null : num(summary.pending),
        availability: availability(summary?.pendingAvailability),
      },
      {
        key: 'FAILED',
        label: 'Failed',
        amount: summary?.failed == null ? null : num(summary.failed),
        availability: availability(summary?.failedAvailability),
      },
      {
        key: 'ON_HOLD',
        label: 'On Hold',
        amount: summary?.onHold == null ? null : num(summary.onHold),
        availability: availability(summary?.onHoldAvailability ?? 'COMING_SOON'),
      },
    ];
  }, [data?.payoutStatusSummary]);

  const opexSeries = useMemo(
    () =>
      (data?.operatingExpenseTrends?.series ?? []).map((series) => ({
        name: series.name ?? 'Series',
        ...(series.category != null ? { category: series.category } : {}),
        total: num(series.total),
        values: (series.values ?? []).map(num),
      })),
    [data?.operatingExpenseTrends?.series],
  );

  const incentives = useMemo(() => {
    const row = data?.creatorIncentives;
    if (!row) return undefined;
    return {
      referralRewards: row.referralRewards == null ? null : num(row.referralRewards),
      referralAvailability: availability(row.referralAvailability),
      creatorBonusesAvailability: availability(row.creatorBonusesAvailability),
      campaignRewardsAvailability: availability(row.campaignRewardsAvailability),
      ...(row.note != null ? { note: row.note } : {}),
    };
  }, [data?.creatorIncentives]);

  const riskMetrics = useMemo(() => {
    const risk = data?.riskOverview;
    if (!risk) return undefined;
    return {
      highRisk: {
        label: risk.highRiskCases?.label ?? 'High Risk',
        value: risk.highRiskCases?.value ?? null,
        availability: availability(risk.highRiskCases?.availability),
      },
      critical: {
        label: risk.criticalAlerts?.label ?? 'Critical Alerts',
        value: risk.criticalAlerts?.value ?? null,
        availability: availability(risk.criticalAlerts?.availability),
      },
      underReview: {
        label: risk.accountsUnderReview?.label ?? 'Under Review',
        value: risk.accountsUnderReview?.value ?? null,
        availability: availability(risk.accountsUnderReview?.availability),
      },
      fraudRate: {
        label: risk.fraudRate?.label ?? 'Fraud Rate',
        value: risk.fraudRate?.value ?? null,
        availability: availability(risk.fraudRate?.availability),
      },
    };
  }, [data?.riskOverview]);

  function exportCurrentView() {
    if (!data) {
      toast.error('Nothing to export yet.');
      return;
    }
    const csv = buildFinanceDashboardCsv({
      currency: reportingCurrency,
      rangeLabel: overviewRangeLabel,
      days,
      countryCode,
      ...(data.startDate ? { startDate: data.startDate } : {}),
      ...(data.endDate ? { endDate: data.endDate } : {}),
      kpis,
      revenueSources: topRevenueSources,
      countryBreakdown: (data.countryGrossBreakdown ?? []).map((row) => ({
        countryCode: row.countryCode ?? 'UNASSIGNED',
        label: row.label ?? financeCountryLabel(row.countryCode ?? 'UNASSIGNED'),
        amount: num(row.amount),
        percentage: num(row.percentage),
      })),
      overviewCategories: data.transactionOverview?.categories ?? [],
      overviewSeries: transactionSeries.map((series) => ({
        name: overviewSeriesDisplayName(series.name, series.category),
        values: series.values,
      })),
      revenueTrendPeriods: data.revenueSourceTrends?.periods ?? [],
      revenueTrendSeries: revenueTrendSeries.map((series) => ({
        name: series.name,
        values: series.values,
      })),
      topCreators: topCreators.map((creator) => ({
        userId: creator.userId,
        displayName: creator.displayName,
        amount: creator.amount,
      })),
      paymentChannels: paymentChannels.map((channel) => ({
        provider: channel.provider,
        label: channel.label,
        gross: channel.gross,
        providerFee: channel.providerFee,
        refundsChargebacks: channel.refundsChargebacks,
        refundsChargebacksAvailability: channel.refundsChargebacksAvailability,
        netSettlement: channel.netSettlement,
        providerFeeAvailability: channel.providerFeeAvailability,
        netSettlementAvailability: channel.netSettlementAvailability,
      })),
      payoutStatus: {
        paid: data.payoutStatusSummary?.paid == null ? null : num(data.payoutStatusSummary.paid),
        pending:
          data.payoutStatusSummary?.pending == null
            ? null
            : num(data.payoutStatusSummary.pending),
        failed:
          data.payoutStatusSummary?.failed == null ? null : num(data.payoutStatusSummary.failed),
        onHold:
          data.payoutStatusSummary?.onHold == null ? null : num(data.payoutStatusSummary.onHold),
      },
      riskUnderReview: data.riskOverview?.accountsUnderReview?.value ?? null,
      operatingExpensePeriods: data.operatingExpenseTrends?.periods ?? [],
      operatingExpenseSeries: opexSeries.map((series) => ({
        name: series.name,
        values: series.values,
      })),
      creatorIncentivesReferral:
        data.creatorIncentives?.referralRewards == null
          ? null
          : num(data.creatorIncentives.referralRewards),
    });
    downloadTextFile(
      financeCsvFilename(reportingCurrency, days, countryCode || undefined),
      csv,
    );
    toast.success('Exported current Finance view as CSV.');
  }

  const startDate = data?.startDate;
  const endDate = data?.endDate;
  const availableCountryCodes = data?.availableCountryCodes ?? [];
  const chartFilters = {
    range,
    onRangeChange: setRange,
    countryCode,
    onCountryCodeChange: setCountryCode,
    availableCountryCodes,
    ...(!dashboardBusy && startDate ? { startDate } : {}),
    ...(!dashboardBusy && endDate ? { endDate } : {}),
    isLoading: dashboardBusy,
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] space-y-4 p-4 sm:p-6 lg:px-8 lg:py-6">
      <header>
        <FinanceFilters
          currency={currency}
          onCurrencyChange={setCurrency}
          range={range}
          onRangeChange={setRange}
          countryCode={countryCode}
          onCountryCodeChange={setCountryCode}
          availableCountryCodes={availableCountryCodes}
          {...(!dashboardBusy && startDate ? { startDate } : {})}
          {...(!dashboardBusy && endDate ? { endDate } : {})}
          onExportCsv={exportCurrentView}
          exportDisabled={!data || dashboardBusy}
        />
      </header>

      {initialLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : initialError ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="size-4 shrink-0" />
            <span>{financeDashboardErrorMessage(dashboardQuery.error)}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => void dashboardQuery.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          {dashboardQuery.isError ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <div className="flex items-center gap-2">
                <AlertCircle className="size-4 shrink-0" />
                <span>{financeDashboardErrorMessage(dashboardQuery.error)}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => void dashboardQuery.refetch()}>
                Retry
              </Button>
            </div>
          ) : null}
          {/* Two columns 3:1 — primary (country + KPIs + charts) | secondary summaries */}
          <div
            className="grid items-start gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]"
            aria-busy={dashboardBusy || undefined}
          >
            <div className="min-w-0 space-y-4">
              {data?.countryGrossBreakdown && data.countryGrossBreakdown.length > 0 && (
                <FinanceRefreshing isLoading={dashboardBusy}>
                  <FinanceCountryGrossBreakdown
                    breakdown={data.countryGrossBreakdown}
                    currency={reportingCurrency}
                    selectedCountryCode={countryCode}
                    onSelectCountryCode={setCountryCode}
                  />
                </FinanceRefreshing>
              )}

              <FinanceRefreshing isLoading={dashboardBusy}>
                <FinanceKpiCards
                metrics={kpis}
                currency={reportingCurrency}
                revenueSources={topRevenueSources}
                payoutStatus={{
                  paid:
                    data?.payoutStatusSummary?.paid == null
                      ? null
                      : num(data.payoutStatusSummary.paid),
                  ...(data?.payoutStatusSummary?.paidAvailability != null
                    ? {
                        paidAvailability: availability(
                          data.payoutStatusSummary.paidAvailability,
                        ),
                      }
                    : {}),
                  pending:
                    data?.payoutStatusSummary?.pending == null
                      ? null
                      : num(data.payoutStatusSummary.pending),
                  ...(data?.payoutStatusSummary?.pendingAvailability != null
                    ? {
                        pendingAvailability: availability(
                          data.payoutStatusSummary.pendingAvailability,
                        ),
                      }
                    : {}),
                  failed:
                    data?.payoutStatusSummary?.failed == null
                      ? null
                      : num(data.payoutStatusSummary.failed),
                  ...(data?.payoutStatusSummary?.failedAvailability != null
                    ? {
                        failedAvailability: availability(
                          data.payoutStatusSummary.failedAvailability,
                        ),
                      }
                    : {}),
                  onHold:
                    data?.payoutStatusSummary?.onHold == null
                      ? null
                      : num(data.payoutStatusSummary.onHold),
                  ...(data?.payoutStatusSummary?.onHoldAvailability != null
                    ? {
                        onHoldAvailability: availability(
                          data.payoutStatusSummary.onHoldAvailability,
                        ),
                      }
                    : { onHoldAvailability: availability('COMING_SOON') }),
                }}
              />
              </FinanceRefreshing>
              {data?.reconciliation && (
                <FinanceRefreshing isLoading={dashboardBusy}>
                  <FinanceReconciliationBanner
                    reconciliation={data.reconciliation}
                    currency={reportingCurrency}
                  />
                </FinanceRefreshing>
              )}

              <TransactionOverviewCard
                categories={data?.transactionOverview?.categories ?? []}
                series={transactionSeries}
                total={num(data?.transactionOverview?.total)}
                changePercent={
                  data?.transactionOverview?.changePercent == null
                    ? null
                    : num(data.transactionOverview.changePercent)
                }
                revenueSharePercent={num(data?.transactionOverview?.revenueSharePercent)}
                payoutSharePercent={num(data?.transactionOverview?.payoutSharePercent)}
                currency={reportingCurrency}
                {...chartFilters}
              />

              <RevenueBreakdownCard
                sources={revenueSources}
                currency={reportingCurrency}
                {...chartFilters}
              />

              <RevenueSourceTrendsCard
                periods={data?.revenueSourceTrends?.periods ?? []}
                series={revenueTrendSeries}
                currency={reportingCurrency}
                {...chartFilters}
              />

              <FinanceRefreshing isLoading={dashboardBusy}>
                <PayoutKpiSummaryCard
                  currency={reportingCurrency}
                  paid={{
                    amount: data?.payoutStatusSummary?.paid == null
                      ? null
                      : num(data.payoutStatusSummary.paid),
                    delta: kpis.find((kpi) => kpi.key === 'PAID_TO_CREATORS')?.delta ?? null,
                    availability: availability(data?.payoutStatusSummary?.paidAvailability),
                  }}
                  pending={{
                    amount: data?.payoutStatusSummary?.pending == null
                      ? null
                      : num(data.payoutStatusSummary.pending),
                    delta: null,
                    availability: availability(data?.payoutStatusSummary?.pendingAvailability),
                  }}
                />
              </FinanceRefreshing>
              <OperatingExpenseTrendsCard
                periods={data?.operatingExpenseTrends?.periods ?? []}
                series={opexSeries}
                currency={reportingCurrency}
                {...chartFilters}
                {...(incentives ? { incentives } : {})}
              />

              <div className="grid items-stretch gap-4 lg:grid-cols-2">
                <FinanceRefreshing isLoading={dashboardBusy}>
                  <PayoutStatusSummaryCard
                    currency={reportingCurrency}
                    slices={payoutStatusSlices}
                    {...(data?.payoutStatusSummary?.note
                      ? { note: data.payoutStatusSummary.note }
                      : {})}
                  />
                </FinanceRefreshing>
                <RefundsChargebacksCard />
              </div>
            </div>

            <div className="min-w-0 space-y-3">
              <FinanceRefreshing isLoading={dashboardBusy}>
                <PaymentChannelSummaryCard channels={paymentChannels} currency={reportingCurrency} />
              </FinanceRefreshing>
              <FinanceRefreshing isLoading={dashboardBusy}>
                <RiskOverviewCard {...(riskMetrics ? { metrics: riskMetrics } : {})} />
              </FinanceRefreshing>
              <FinanceRefreshing isLoading={dashboardBusy}>
                <TopRevenueSourcesCard sources={topRevenueSources} currency={reportingCurrency} />
              </FinanceRefreshing>
              <FinanceRefreshing isLoading={dashboardBusy}>
                <TopCreatorsCard creators={topCreators} currency={reportingCurrency} />
              </FinanceRefreshing>
            </div>
          </div>
        </>
      )}

      {data?.dataSourceNote ? (
        <p className="text-[11px] text-muted-foreground">{data.dataSourceNote}</p>
      ) : null}
    </div>
  );
}
